/**
 * feeEditorUtils.ts
 * Shared utility functions and constants for the fee configuration editor.
 *
 * ARCHITECTURAL CONSTRAINTS:
 * - Only `import type` from `types/` (no runtime dependencies from types)
 * - No side effects, pure data and functions only
 */

// Internal absolute imports (type-only)
import type { CommissionType, CommissionTiming, ConversionRateType, OvernightAssetClass, VerificationStatus } from '@/types/shared/FeeEnums';
import type { CommissionStructure } from '@/types/shared/feeStructures/CommissionStructure';
import type { CurrencyConversionStructure } from '@/types/shared/feeStructures/CurrencyConversionStructure';

// =============================================================================
// Constants
// =============================================================================

/** Commission types that use the common feeAmount/feePrecision fields */
export const TYPES_WITH_FEE_AMOUNT = new Set<CommissionType>([
  'fixedPerUnit',
  'fixedPerTransaction',
  'percentage',
  'perShare',
  'perSharePlusBase',
]);

/** Human-readable labels for the feeAmount field, keyed by commission type */
export const FEE_AMOUNT_LABEL_MAP: Readonly<Partial<Record<CommissionType, string>>> = {
  fixedPerUnit: 'Fee Per Unit',
  fixedPerTransaction: 'Fee Per Transaction',
  percentage: 'Fee Rate',
  perShare: 'Fee Per Share',
  perSharePlusBase: 'Fee Per Share',
} as const;

/** Help text descriptions for CommissionTiming options in the Charge On dropdown */
export const COMMISSION_TIMING_DESCRIPTION_MAP: Readonly<Record<CommissionTiming, string>> = {
  roundTrip: 'Total fee charged on close',
  bothLegs: 'Half on open, half on close',
  perSide: 'Full fee on each side',
} as const;

/** Chip color for each verification status */
export const VERIFICATION_STATUS_COLOR_MAP: Readonly<Record<VerificationStatus, 'warning' | 'default' | 'success' | 'primary'>> = {
  assumed: 'warning',
  template: 'warning',
  verified: 'default',
  userVerified: 'success',
  userOverride: 'primary',
} as const;

// =============================================================================
// Formatting
// =============================================================================

/**
 * Format an integer fee amount with its precision into a human-readable string.
 *
 * @example
 * formatFeeAmount(50, 2)          // "0.50"
 * formatFeeAmount(125, 2)         // "1.25"
 * formatFeeAmount(10, 4)          // "0.0010"
 * formatFeeAmount(undefined, undefined) // "—"
 * formatFeeAmount(0, 2)           // "0.00"
 */
export function formatFeeAmount(
  value: number | undefined,
  precision: number | undefined
): string {
  if (value === undefined || value === null) return '—';
  const p = precision ?? 2;
  const divisor = Math.pow(10, p);
  return (value / divisor).toFixed(p);
}

// =============================================================================
// Precision Rescaling
// =============================================================================

/**
 * Rescale a stored integer when precision changes so the displayed decimal
 * stays constant.
 *
 * @example
 * rescaleForPrecision(165, 2, 4)  // 16500  (display stays "1.65" → "1.6500")
 * rescaleForPrecision(165, 4, 2)  // 2      (display stays "0.0165" → "0.02")
 * rescaleForPrecision(undefined, 2, 4) // undefined
 */
export function rescaleForPrecision(
  storedValue: number | undefined,
  oldPrecision: number,
  newPrecision: number
): number | undefined {
  if (storedValue === undefined || storedValue === null) return undefined;
  const diff = newPrecision - oldPrecision;
  if (diff === 0) return storedValue;
  return Math.round(storedValue * Math.pow(10, diff));
}

// =============================================================================
// Overnight Asset Class Derivation
// =============================================================================

/** Market segments that map to the 'commodities' overnight asset class */
const COMMODITY_SEGMENTS = new Set(['commodities', 'metals', 'energies', 'softs', 'meats']);

/**
 * Derive the OvernightAssetClass from a fee tree node's categoryPath.
 *
 * The overnight asset class determines which calculation methodology is used
 * for overnight/swap fees. It's derived from the fee tree context rather than
 * being user-editable, since the tree already scopes the fee configuration
 * to a specific asset class and market segment.
 *
 * @param categoryPath - Fee tree node's category path (e.g., ['futures', 'indices', 'micro'])
 * @returns The derived OvernightAssetClass for overnight fee calculation
 *
 * @example
 * deriveOvernightAssetClass(['forex', 'majors'])       // 'forex'
 * deriveOvernightAssetClass(['futures', 'indices'])     // 'indices'
 * deriveOvernightAssetClass(['futures', 'metals'])      // 'commodities'
 * deriveOvernightAssetClass(['cfd', 'indices'])         // 'indices'
 * deriveOvernightAssetClass(['stocks'])                 // 'stocks'
 * deriveOvernightAssetClass(['bonds'])                  // 'stocks'
 * deriveOvernightAssetClass([])                         // 'forex'
 */
export function deriveOvernightAssetClass(categoryPath: string[]): OvernightAssetClass {
  const assetClassType = categoryPath[0];

  switch (assetClassType) {
    // Direct mappings
    case 'forex':
      return 'forex';
    case 'stocks':
      return 'stocks';
    case 'crypto':
      return 'crypto';

    // Segment-dependent: underlying determines calculation methodology
    case 'futures':
    case 'cfd': {
      const segment = categoryPath[1];
      if (segment === 'indices') return 'indices';
      if (segment && COMMODITY_SEGMENTS.has(segment)) return 'commodities';
      // Default for futures/cfd without recognizable segment
      return 'indices';
    }

    // Position-value-based calculation (similar to stocks)
    case 'bonds':
    case 'options':
    case 'funds':
      return 'stocks';

    // Fallback for empty path or unrecognized asset class type
    default:
      return 'forex';
  }
}

// =============================================================================
// Type Switching
// =============================================================================

/**
 * Build a new CommissionStructure when the commission type changes.
 *
 * Preserves common fields (chargeOn, min/maxPerOrder, verification) and
 * feeAmount/feePrecision when switching between types that use them.
 * Clears all type-specific fields from the old type and initializes
 * type-specific fields for the new type where appropriate.
 */
export function buildSwitchedCommission(
  current: CommissionStructure,
  newType: CommissionType
): CommissionStructure {
  // Start with common fields that are always preserved
  const result: CommissionStructure = {
    feeType: newType,
    chargeOn: newType === 'spreadOnly' ? 'perSide' : current.chargeOn,
    verificationStatus: current.verificationStatus,
  };

  // Preserve verification metadata
  if (current.lastVerifiedAt !== undefined) {
    result.lastVerifiedAt = current.lastVerifiedAt;
  }
  if (current.verificationNotes !== undefined) {
    result.verificationNotes = current.verificationNotes;
  }

  // Preserve min/max per order (except for spreadOnly which has no commission)
  if (newType !== 'spreadOnly') {
    if (current.minPerOrder !== undefined) {
      result.minPerOrder = current.minPerOrder;
    }
    if (current.minPerOrderPrecision !== undefined) {
      result.minPerOrderPrecision = current.minPerOrderPrecision;
    }
    if (current.maxPerOrder !== undefined) {
      result.maxPerOrder = current.maxPerOrder;
    }
    if (current.maxPerOrderPrecision !== undefined) {
      result.maxPerOrderPrecision = current.maxPerOrderPrecision;
    }
  }

  // Preserve feeAmount/feePrecision when both old and new types use them
  if (
    TYPES_WITH_FEE_AMOUNT.has(current.feeType) &&
    TYPES_WITH_FEE_AMOUNT.has(newType)
  ) {
    if (current.feeAmount !== undefined) {
      result.feeAmount = current.feeAmount;
    }
    if (current.feePrecision !== undefined) {
      result.feePrecision = current.feePrecision;
    }
  }

  // Default feePrecision to 2 for currency-denominated types where the precision
  // input is hidden. Only reset when switching FROM percentage (stale bps precision)
  // or when feePrecision was never set. Preserves intentional non-percentage precision.
  if (
    TYPES_WITH_FEE_AMOUNT.has(newType) &&
    newType !== 'percentage' &&
    (current.feeType === 'percentage' || result.feePrecision === undefined)
  ) {
    result.feePrecision = 2;
  }

  // Initialize type-specific fields for the new type
  if (newType === 'tiered') {
    result.tiers = [{ threshold: 0, feeAmount: 0, feePrecision: 2 }];
  }

  return result;
}

/**
 * Build a new CurrencyConversionStructure when the rate type changes.
 *
 * Preserves common fields (triggers, usesRealTimeRates, min/max fees,
 * threshold, applicableCurrencyPairs, conversionNotes, verification) and
 * clears type-specific fields from the old type. Initializes defaults for
 * type-specific fields of the new type where appropriate.
 */
export function buildSwitchedConversion(
  current: CurrencyConversionStructure,
  newType: ConversionRateType
): CurrencyConversionStructure {
  // Start with common fields that are always preserved
  const result: CurrencyConversionStructure = {
    rateType: newType,
    triggers: [...(current.triggers ?? [])],
    usesRealTimeRates: current.usesRealTimeRates,
    verificationStatus: current.verificationStatus,
  };

  // Preserve verification metadata
  if (current.lastVerifiedAt !== undefined) {
    result.lastVerifiedAt = current.lastVerifiedAt;
  }
  if (current.verificationNotes !== undefined) {
    result.verificationNotes = current.verificationNotes;
  }

  // Preserve min/max fee per transaction
  if (current.minFeePerTransaction !== undefined) {
    result.minFeePerTransaction = current.minFeePerTransaction;
  }
  if (current.minFeePerTransactionPrecision !== undefined) {
    result.minFeePerTransactionPrecision = current.minFeePerTransactionPrecision;
  }
  if (current.maxFeePerTransaction !== undefined) {
    result.maxFeePerTransaction = current.maxFeePerTransaction;
  }
  if (current.maxFeePerTransactionPrecision !== undefined) {
    result.maxFeePerTransactionPrecision = current.maxFeePerTransactionPrecision;
  }

  // Preserve trade value threshold
  if (current.minTradeValueThreshold !== undefined) {
    result.minTradeValueThreshold = current.minTradeValueThreshold;
  }
  if (current.minTradeValueThresholdPrecision !== undefined) {
    result.minTradeValueThresholdPrecision = current.minTradeValueThresholdPrecision;
  }

  // Preserve rateUpdateFrequencyMinutes (no UI currently — deferred until FX rate integration)
  if (current.rateUpdateFrequencyMinutes !== undefined) {
    result.rateUpdateFrequencyMinutes = current.rateUpdateFrequencyMinutes;
  }

  // Preserve applicable currency pairs
  if (current.applicableCurrencyPairs !== undefined) {
    result.applicableCurrencyPairs = [...current.applicableCurrencyPairs];
  }

  // Preserve conversion notes
  if (current.conversionNotes !== undefined) {
    result.conversionNotes = current.conversionNotes;
  }

  // Initialize type-specific fields for the new type
  if (newType === 'spotPlusMarkup') {
    // Carry over markup if switching between markup-using types (currently only spotPlusMarkup)
    if (current.rateType === 'spotPlusMarkup') {
      if (current.markup !== undefined) result.markup = current.markup;
      if (current.markupPrecision !== undefined) result.markupPrecision = current.markupPrecision;
    }
    // Default markupPrecision to 4 (basis points) if unset
    if (result.markupPrecision === undefined) {
      result.markupPrecision = 4;
    }
  }

  if (newType === 'fixed') {
    // Carry over fixedRate if switching between fixed types (currently only fixed)
    if (current.rateType === 'fixed') {
      if (current.fixedRate !== undefined) result.fixedRate = current.fixedRate;
      if (current.fixedRatePrecision !== undefined) result.fixedRatePrecision = current.fixedRatePrecision;
    }
    // Default fixedRatePrecision to 4 (exchange rates) if unset
    if (result.fixedRatePrecision === undefined) {
      result.fixedRatePrecision = 4;
    }
  }

  return result;
}
